import {
    createContext,
    FunctionComponent,
    ReactNode,
    useEffect,
    useState
} from 'react';
import {
    AuthUser,
    fetchAuthSession,
    getCurrentUser,
    JWT
} from 'aws-amplify/auth';
import type {AuthStatus} from '@flexinet/auth-components';


type AuthContextType = {
    user: AuthUser | undefined,
    // FNP-1202: distinguishes "no user because we have not asked Cognito yet"
    // from "no user because nobody is signed in". Without it, `user ===
    // undefined` on first paint is indistinguishable from signed-out, and
    // every auth guard bounced deep links before the session resolved.
    authResolved: boolean,
    // FNP-1729: the shared VerifiedPermissionsProvider's `sessionStatus`.
    // 'resolving' until BOTH the user and the session lookups settle, then
    // 'authenticated' or 'anonymous'. Waiting on the session too means the
    // provider never evaluates permissions before the tokens are known.
    status: AuthStatus,
    idToken?: JWT | undefined,
    accessToken?: JWT | undefined,
    cognitoGroups?: string[] | undefined,
    role?: string | undefined,
}


// FNP-960: a context object and its provider component live together by
// design; splitting them would churn every import for a dev-only
// hot-reload nicety. Same suppression billing-app carries on its
// byte-identical AuthContext.tsx — platform precedent, not invented here.
// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType>({
                                                              user: {} as AuthUser,
                                                              authResolved: false,
                                                              // FNP-1729: never 'anonymous' by default. Outside
                                                              // the provider nobody has asked Cognito yet.
                                                              status: 'resolving',
                                                          });

interface IAuthProvider {
    children?: ReactNode
}

export const AuthProvider: FunctionComponent<IAuthProvider> = ({children}) => {

    const [user, setUser] = useState<AuthUser | undefined>();
    const [idToken, setIdToken] = useState<JWT | undefined>();
    const [accessToken, setAccessToken] = useState<JWT | undefined>();
    const [cognitoGroups, setCognitoGroups] = useState<string[] | undefined>();
    const [role, setRole] = useState<string | undefined>();
    const [authResolved, setAuthResolved] = useState(false);
    const [sessionResolved, setSessionResolved] = useState(false);

    useEffect(() => {
        const fetchCognitoUser = async () => {

            try {
                const result = await getCurrentUser()

                const {
                    username,
                    userId,
                    signInDetails
                } = result

                setUser({
                            username,
                            userId,
                            signInDetails
                        })

            } catch (error) {
                console.trace(
                    'AUTH CONTEXT. FETCH USER ERROR',
                    error
                );
            } finally {
                // FNP-1202: set in `finally` so a signed-out visitor resolves
                // too. Only the success path would leave guards waiting forever
                // and never render the public site.
                setAuthResolved(true);
            }
        };

        fetchCognitoUser();
    }, []);


    useEffect(() => {
        const fetchSession = async () => {

            try {
                const {
                    accessToken,
                    idToken
                } = (await fetchAuthSession()).tokens ?? {};

                setIdToken(idToken);
                setAccessToken(accessToken);
                if (typeof idToken !== 'undefined'
                    && idToken.payload['cognito:groups'] !== undefined
                    && Array.isArray(idToken.payload['cognito:groups'])
                    && idToken.payload['cognito:groups'].length > 0) {
                    setCognitoGroups(idToken.payload['cognito:groups'] as string[]);
                }

            } catch (error) {
                console.trace(
                    'AUTH CONTEXT. FETCH SESSION ERROR',
                    error
                );
            } finally {
                setSessionResolved(true);
            }

        };

        fetchSession();
    }, []);

    useEffect(() => {

        if (typeof idToken !== 'undefined'
            && idToken.payload['cognito:groups'] !== undefined
            && Array.isArray(idToken.payload['cognito:groups'])
            && idToken.payload['cognito:groups'].length > 0) {

            const adminItem = idToken.payload['cognito:groups'].find((item: unknown) => item === 'admin')
            if (adminItem) {
                setRole('Admin')
            }

        }

    }, [idToken])

    const status: AuthStatus = !authResolved || !sessionResolved
        ? 'resolving'
        : user ? 'authenticated' : 'anonymous';


    return (
        <AuthContext.Provider value={{
            user,
            authResolved,
            status,
            idToken,
            accessToken,
            cognitoGroups,
            role
        }}>
            {children}
        </AuthContext.Provider>
    );
};

